import { Hammer } from "@phosphor-icons/react";
import { highestEarnedMilestone, MILESTONE_COPY, SIGNATURE_MILESTONES } from "@/lib/milestoneCopy";

/**
 * Persistent encouragement card placed in the Steps tab near the current step.
 * Renders only when the user has crossed at least one milestone. Derived from
 * `progress_pct` + `steps_done` on the project payload — NOT from the toast/
 * sheet dismissal state. So dismissing the immediate celebration doesn't
 * remove this card; it stays as a quiet reminder of how far the user has
 * already come.
 *
 * Tries to balance Half Job's voice (warm + cheeky) with not getting in the
 * way of the safety/blocked-step cards or stepping on the cover photo.
 */
export default function InlineEncouragement({ progressPct, stepsDone }) {
  const key = highestEarnedMilestone({ progressPct, stepsDone });
  if (!key) return null;

  const copy = MILESTONE_COPY[key];
  if (!copy) return null;

  const isSignature = SIGNATURE_MILESTONES.has(key);
  const isComplete = key === "p100";

  return (
    <div
      data-testid={`inline-encouragement-${key}`}
      role="status"
      className={[
        "rounded-2xl border-2 p-4 flex items-start gap-3 shadow-tactile",
        isComplete
          ? "bg-ok/10 border-ok"
          : isSignature
          ? "bg-masking border-timber-900"
          : "bg-masking/15 border-masking",
      ].join(" ")}
    >
      <span
        className={[
          "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center",
          isSignature ? "bg-timber-900" : "bg-chalk border-2 border-masking",
        ].join(" ")}
        aria-hidden="true"
      >
        <Hammer
          size={22}
          weight={isSignature ? "fill" : "duotone"}
          className={isSignature ? "text-masking" : "text-masking"}
        />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={[
            "font-heading font-bold leading-snug",
            isSignature ? "text-lg text-timber-900" : "text-base text-timber-900",
          ].join(" ")}
        >
          {copy.headline}
        </p>
        <p
          className={[
            "mt-1 leading-snug whitespace-pre-line",
            isSignature ? "text-sm text-timber-900/85" : "text-xs text-timber-700",
          ].join(" ")}
        >
          {copy.sub}
        </p>
      </div>
    </div>
  );
}
